import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Skeleton,
  StatusPill,
  toast,
} from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import { usePermission } from "../../../lib/permissions.ts";
import {
  SAMPLE_PHASE_LABEL,
  SAMPLE_PHASE_TONE,
  SAMPLE_PURPOSE_LABEL,
  exactMoney,
  shortDate,
} from "./sample-format.ts";

/** One labelled fact in the side rail. */
function RailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1.5">
      <span className="text-xs text-muted">{label}</span>
      <span className="text-right text-sm text-ink">{value}</span>
    </div>
  );
}

/**
 * Store admin — Sample detail (Commerce, M3).
 *
 * The one screen where a sample moves along its lifecycle. Dispatch is the
 * interesting step: it takes the courier, the AWB and — critically — the return
 * date, because that date is the only thing that makes "overdue" computable.
 * Without it a sample can sit out forever and never appear on the Overdue tab,
 * so the field is required to dispatch.
 *
 * @see packages/domain/src/commerce/store/sample-transitions.ts
 */
export function SampleDetailPage({ sampleId }: { sampleId: string }) {
  const utils = trpc.useUtils();
  const q = trpc.storeSamples.get.useQuery({ sampleId });
  const canAct = usePermission("store.orders.approve");

  const [courier, setCourier] = useState("");
  const [awb, setAwb] = useState("");
  const [returnBy, setReturnBy] = useState("");

  const transition = trpc.storeSamples.transition.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.storeSamples.get.invalidate({ sampleId }),
        utils.storeSamples.list.invalidate(),
        utils.storeSamples.counts.invalidate(),
      ]);
      toast.success("Sample updated");
    },
    onError: (err) => toast.error("Couldn't update the sample", { description: storeErrorMessage(err) }),
  });

  const s = q.data;

  if (q.isLoading) {
    return (
      <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Sampling" }]}>
        <StorePageColumn variant="grid">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="mt-4 h-64 w-full" />
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  if (!s) {
    return (
      <StoreAdminLayout breadcrumbs={[{ label: "Store", to: "/store/admin" }, { label: "Sampling" }]}>
        <StorePageColumn variant="grid">
          <BackButton fallbackTo="/store/admin/sampling" label="Back to sampling" />
          <p className="mt-6 text-sm text-muted">That sample doesn&rsquo;t exist.</p>
        </StorePageColumn>
      </StoreAdminLayout>
    );
  }

  const dispatchReady = returnBy.trim().length > 0;

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Sampling", to: "/store/admin/sampling" },
        { label: s.docNumber || "Unnumbered" },
      ]}
    >
      <StorePageColumn variant="grid">
        <BackButton fallbackTo="/store/admin/sampling" label="Back to sampling" />

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-mono text-2xl font-bold tracking-tight text-ink leading-tight">
                {s.docNumber || "Unnumbered sample"}
              </h1>
              <StatusPill tone={SAMPLE_PHASE_TONE[s.phase]} size="sm" dot>
                {SAMPLE_PHASE_LABEL[s.phase]}
              </StatusPill>
            </div>
            <p className="mt-1.5 text-sm text-muted">
              {s.customerName} · Requested {shortDate(s.requestedAt)}
              {s.buyerId ? " · from the storefront" : " · raised by staff"}
            </p>
          </div>

          {canAct ? (
            <div className="flex flex-wrap items-center gap-2">
              {s.status === "delivered" ? (
                <>
                  <Button
                    onClick={() => transition.mutate({ sampleId, status: "converted" })}
                    disabled={transition.isPending}
                  >
                    Mark converted
                  </Button>
                  <Button
                    intent="secondary"
                    onClick={() => transition.mutate({ sampleId, status: "returned" })}
                    disabled={transition.isPending}
                  >
                    Mark returned
                  </Button>
                </>
              ) : null}
              {s.status === "dispatched" ? (
                <Button
                  onClick={() => transition.mutate({ sampleId, status: "delivered" })}
                  disabled={transition.isPending}
                >
                  Mark delivered
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Dispatch is a form, not a button: the return date is what makes
            "overdue" computable, so it is collected at the moment goods leave
            rather than left to be filled in later (and never filled in). */}
        {canAct && s.status === "requested" ? (
          <Card className="mt-4">
            <CardHeader>
              <h2 className="text-sm font-semibold text-ink">Dispatch this sample</h2>
            </CardHeader>
            <CardBody>
              <div className="grid gap-3 sm:grid-cols-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-muted">Courier</span>
                  <Input value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="Blue Dart" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-muted">AWB</span>
                  <Input value={awb} onChange={(e) => setAwb(e.target.value)} placeholder="1234567890" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-medium text-muted">Return by *</span>
                  <Input type="date" value={returnBy} onChange={(e) => setReturnBy(e.target.value)} />
                </label>
              </div>
              <p className="mt-2 text-xs text-muted">
                The return date is what puts this sample on the Overdue tab if it doesn&rsquo;t come
                back. Dispatching also allocates its document number.
              </p>
              <div className="mt-3">
                <Button
                  onClick={() =>
                    transition.mutate({
                      sampleId,
                      status: "dispatched",
                      ...(courier.trim() ? { courier: courier.trim() } : {}),
                      ...(awb.trim() ? { awb: awb.trim() } : {}),
                      returnBy,
                    })
                  }
                  disabled={!dispatchReady || transition.isPending}
                >
                  Mark dispatched
                </Button>
              </div>
            </CardBody>
          </Card>
        ) : null}

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader>
              <h2 className="text-sm font-semibold text-ink">Pieces requested</h2>
            </CardHeader>
            <CardBody className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="border-b border-line bg-raised text-xs uppercase tracking-wide text-muted">
                    <tr>
                      <th className="px-3 py-3 text-left font-medium">Product</th>
                      <th className="px-3 py-3 text-left font-medium">SKU</th>
                      <th className="px-3 py-3 text-right font-medium">Pieces</th>
                      <th className="px-3 py-3 text-left font-medium">Return?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.lines.map((l) => (
                      <tr key={l.lineId} className="border-b border-line last:border-0">
                        <td className="px-3 py-3 text-ink">{l.itemName ?? "—"}</td>
                        <td className="px-3 py-3 font-mono text-xs text-ink-2">{l.sku ?? "—"}</td>
                        <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
                          {l.qty}
                        </td>
                        <td className="px-3 py-3 text-muted">
                          {l.returnExpected ? "Expected back" : "Giveaway"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Details</h2>
              </CardHeader>
              <CardBody>
                <RailRow label="Value out" value={exactMoney(s.valuePaise)} />
                <RailRow label="Purpose" value={SAMPLE_PURPOSE_LABEL[s.purpose]} />
                <RailRow label="Return by" value={s.returnBy ?? "—"} />
                <RailRow label="Dispatched" value={shortDate(s.dispatchedAt)} />
                <RailRow label="Returned" value={shortDate(s.returnedAt)} />
                <RailRow label="Courier" value={s.courier ?? "—"} />
                <RailRow label="AWB" value={s.awb ?? "—"} />
                {s.convertedOrderId ? (
                  <div className="mt-2 border-t border-line pt-2">
                    <Link
                      to="/store/admin/orders/$orderId"
                      params={{ orderId: s.convertedOrderId }}
                      className="text-sm font-medium text-sky-700"
                    >
                      View the order this became →
                    </Link>
                  </div>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold text-ink">Ship to</h2>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-ink-2">{s.shipToText ?? "—"}</p>
                {s.notesForBuyer ? (
                  <p className="mt-3 border-t border-line pt-3 text-sm text-muted">
                    <span className="font-medium text-ink-2">They asked: </span>
                    {s.notesForBuyer}
                  </p>
                ) : null}
              </CardBody>
            </Card>
          </div>
        </div>
      </StorePageColumn>
    </StoreAdminLayout>
  );
}
